import {
  useState,
  useMemo,
  useCallback,
  forwardRef,
  Children,
  cloneElement,
  isValidElement,
} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
  Box,
  Typography,
  Chip,
  createFilterOptions,
} from '@mui/material';
import { CheckBoxOutlineBlank, CheckBox } from '@mui/icons-material';
import { List, type RowComponentProps } from 'react-window';
import COLORS from '@/resources/style/Themes';
import { OrgUser } from '@/models/User/OrgUser';
import Group from '@/models/Organization/Group';
import { StringKeys } from '@/resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';

const icon = <CheckBoxOutlineBlank fontSize="small" />;
const checkedIcon = <CheckBox fontSize="small" />;

const ITEM_HEIGHT = 52; // Height for regular items
const GROUP_HEADER_HEIGHT = 48; // Height for group headers

// Helper to flatten grouped children structure from MUI Autocomplete
function flattenChildren(
  children: React.ReactNode[],
): Array<{ element: React.ReactNode; isGroupHeader: boolean }> {
  const flattened: Array<{ element: React.ReactNode; isGroupHeader: boolean }> =
    [];

  children.forEach((child) => {
    if (!isValidElement(child)) return;

    // Check if this is a group container (has nested ul with children)
    const childProps = child.props as { children?: React.ReactNode };
    if (childProps?.children) {
      const childrenArray = Array.isArray(childProps.children)
        ? childProps.children
        : [childProps.children];

      // Look for group header (Box with sticky positioning)
      const groupHeader = childrenArray.find(
        (c: React.ReactNode) =>
          isValidElement(c) &&
          typeof c.type !== 'string' &&
          (c.props as { sx?: { position?: string } })?.sx?.position ===
            'sticky',
      );

      // Look for nested list (Box with component="ul")
      const nestedList = childrenArray.find(
        (c: React.ReactNode) =>
          isValidElement(c) &&
          (c.props as { component?: string })?.component === 'ul',
      );

      if (groupHeader) {
        flattened.push({ element: groupHeader, isGroupHeader: true });
      }

      if (nestedList && isValidElement(nestedList)) {
        const nestedChildren = Children.toArray(
          (nestedList.props as { children?: React.ReactNode }).children,
        );
        nestedChildren.forEach((nestedChild) => {
          flattened.push({ element: nestedChild, isGroupHeader: false });
        });
      }
    } else {
      // Regular item without grouping
      flattened.push({ element: child, isGroupHeader: false });
    }
  });

  return flattened;
}

// Row component for virtualized list
function VirtualizedRow({
  index,
  style,
  itemData,
}: RowComponentProps<{
  itemData: Array<{ element: React.ReactNode; isGroupHeader: boolean }>;
}>) {
  const item = itemData[index];
  const element = item.element;

  // Clone the element and merge the positioning styles from react-window
  if (isValidElement(element)) {
    const existingStyle =
      (element.props as { style?: React.CSSProperties }).style || {};

    // For group headers, we need to wrap them in an li element
    if (item.isGroupHeader) {
      return (
        <li style={style}>
          {cloneElement<React.HTMLAttributes<HTMLElement>>(
            element as React.ReactElement<React.HTMLAttributes<HTMLElement>>,
            {
              style: {
                ...existingStyle,
                position: 'relative', // Override sticky positioning in virtualized context
                top: 0,
              },
            },
          )}
        </li>
      );
    }

    // Regular items
    return cloneElement<React.HTMLAttributes<HTMLElement>>(
      element as React.ReactElement<React.HTMLAttributes<HTMLElement>>,
      {
        style: {
          ...existingStyle,
          ...style,
        },
      },
    );
  }

  return <div style={style}>{element}</div>;
}

// Virtualized listbox component for better performance with large lists
const ListboxComponent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement>
>(function ListboxComponent(props, ref) {
  const { children, role, ...other } = props;
  const childrenArray = Children.toArray(children);
  const itemData = flattenChildren(childrenArray);
  const itemCount = itemData.length;

  // Calculate row height based on whether it's a group header or regular item
  const getRowHeight = (index: number) => {
    return itemData[index].isGroupHeader ? GROUP_HEADER_HEIGHT : ITEM_HEIGHT;
  };

  return (
    <div ref={ref} role={role} {...other}>
      <List
        rowComponent={VirtualizedRow}
        rowCount={itemCount}
        rowHeight={getRowHeight}
        rowProps={{ itemData }}
        overscanCount={20}
        style={{ maxHeight: 300, width: '100%' }}
      />
    </div>
  );
});

interface UserOption {
  auth_id: string;
  first_name: string;
  last_name: string;
  phone_number: string;
  groupName: string;
  groupId: string;
  isGroupHeader?: boolean;
  isExistingResponder?: boolean;
}

interface AddUserContactsModalProps {
  show: boolean;
  onHide: () => void;
  onAdd: (authIds: string[]) => Promise<void>;
  groups: Group[];
  currentGroupId: string;
  existingContactAuthIds: string[];
  loading?: boolean;
}

const AddUserContactsModal = ({
  show,
  onHide,
  onAdd,
  groups,
  currentGroupId,
  existingContactAuthIds,
  loading = false,
}: AddUserContactsModalProps) => {
  const [selectedUsers, setSelectedUsers] = useState<UserOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const { t } = useTranslation();

  // Build options from all groups, marking users already in emergency contacts as existing responders
  const options = useMemo(() => {
    const userOptions: UserOption[] = [];
    const existingSet = new Set(existingContactAuthIds);

    // Sort groups so current group is first
    const sortedGroups = [...groups].sort((a, b) => {
      if (a.group_id === currentGroupId) return -1;
      if (b.group_id === currentGroupId) return 1;
      return a.group_name.localeCompare(b.group_name);
    });

    for (const group of sortedGroups) {
      // Skip empty groups
      if (group.group_value.length === 0) continue;

      // Create a group header option for selecting all users in the group
      const groupHeader: UserOption = {
        auth_id: `group_header_${group.group_id}`,
        first_name: `${t(StringKeys.SELECT_ALL_IN)} ${group.group_name}`,
        last_name: '',
        phone_number: '',
        groupName: group.group_name,
        groupId: group.group_id,
        isGroupHeader: true,
      };
      userOptions.push(groupHeader);

      // Add individual users, marking existing responders
      for (const user of group.group_value as OrgUser[]) {
        userOptions.push({
          auth_id: user.auth_id,
          first_name: user.first_name,
          last_name: user.last_name,
          phone_number: user.phone_number,
          groupName: group.group_name,
          groupId: group.group_id,
          isGroupHeader: false,
          isExistingResponder: existingSet.has(user.auth_id),
        });
      }
    }

    return userOptions;
  }, [groups, currentGroupId, existingContactAuthIds, t]);

  // Custom filter function - search by name and phone number
  const filterOptions = useMemo(
    () =>
      createFilterOptions<UserOption>({
        stringify: (option) =>
          option.isGroupHeader
            ? option.groupName
            : `${option.first_name} ${option.last_name} ${option.phone_number}`,
      }),
    [],
  );

  // Pre-compute group selection status to avoid O(n*m) complexity on every render
  // This dramatically improves performance when selecting items with large datasets
  const groupSelectionStatus = useMemo(() => {
    const selectedAuthIds = new Set(selectedUsers.map((u) => u.auth_id));
    const statusMap = new Map<
      string,
      { total: number; selected: number; isGroupHeader: boolean }
    >();

    // Build a map of group statistics
    options.forEach((opt) => {
      if (!statusMap.has(opt.groupId)) {
        statusMap.set(opt.groupId, {
          total: 0,
          selected: 0,
          isGroupHeader: false,
        });
      }

      const stats = statusMap.get(opt.groupId)!;

      // Track if we've seen the group header
      if (opt.isGroupHeader) {
        stats.isGroupHeader = true;
      } else if (!opt.isExistingResponder) {
        // Only count selectable users (not headers, not existing responders)
        stats.total++;
        if (selectedAuthIds.has(opt.auth_id)) {
          stats.selected++;
        }
      }
    });

    return statusMap;
  }, [options, selectedUsers]);

  // Check if all users in a group are selected - now O(1) lookup
  const isGroupFullySelected = useCallback(
    (groupId: string) => {
      const stats = groupSelectionStatus.get(groupId);
      return stats && stats.total > 0 && stats.selected === stats.total;
    },
    [groupSelectionStatus],
  );

  // Handle selection change with group header logic
  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: UserOption[], reason: string) => {
      if (reason === 'selectOption') {
        const lastSelected = newValue[newValue.length - 1];

        // If a group header was selected, toggle between select all/deselect all
        if (lastSelected?.isGroupHeader) {
          const groupUsers = options.filter(
            (opt) =>
              opt.groupId === lastSelected.groupId &&
              !opt.isGroupHeader &&
              !opt.isExistingResponder,
          );

          // Check if all users in the group are currently selected
          const allGroupUsersSelected = isGroupFullySelected(
            lastSelected.groupId,
          );

          if (allGroupUsersSelected) {
            const groupUserAuthIds = new Set(groupUsers.map((u) => u.auth_id));
            setSelectedUsers(
              selectedUsers.filter((u) => !groupUserAuthIds.has(u.auth_id)),
            );
          } else {
            const existingAuthIds = new Set(
              selectedUsers.map((u) => u.auth_id),
            );
            const usersToAdd = groupUsers.filter(
              (u) => !existingAuthIds.has(u.auth_id),
            );
            setSelectedUsers([...selectedUsers, ...usersToAdd]);
          }
          return;
        }
      }

      // Filter out any group headers from the final selection
      setSelectedUsers(newValue.filter((u) => !u.isGroupHeader));
    },
    [options, selectedUsers, isGroupFullySelected],
  );

  // Handle add button click
  const handleAdd = async () => {
    const authIds = selectedUsers
      .filter((u) => !u.isGroupHeader)
      .map((u) => u.auth_id);

    if (authIds.length > 0) {
      await onAdd(authIds);
      setSelectedUsers([]);
      setInputValue('');
    }
  };

  // Handle modal close - prevent backdrop clicks from closing the modal
  const handleClose = (_event: object, reason?: string) => {
    // Don't close modal on backdrop click
    if (reason === 'backdropClick') {
      return;
    }

    // Close modal for other reasons (ESC key, etc.)
    setSelectedUsers([]);
    setInputValue('');
    onHide();
  };

  return (
    <Dialog
      open={show}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      slotProps={{
        paper: {
          sx: {
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      <DialogTitle
        sx={{ backgroundColor: COLORS.SAFEGUARD_BLUE, color: COLORS.WHITE }}
      >
        {t(StringKeys.ADD_NEW_RESPONDER)}
      </DialogTitle>
      <DialogContent
        sx={{
          pt: 3,
          minHeight: 300,
          overflow: 'visible',
          flex: '1 1 auto',
        }}
      >
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'flex-start',
          }}
        >
          {/* Left column: Search input */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Autocomplete
              multiple
              limitTags={3}
              id="add-responder-autocomplete"
              options={options}
              value={selectedUsers}
              onChange={handleChange}
              inputValue={inputValue}
              onInputChange={(_, newInputValue, reason) => {
                // Keep the search term visible when selecting items
                if (reason === 'input') {
                  setInputValue(newInputValue);
                }
              }}
              disableCloseOnSelect
              clearOnBlur={true}
              blurOnSelect={false}
              slots={{
                listbox: ListboxComponent,
              }}
              slotProps={{
                popper: {
                  placement: 'bottom-start',
                  modifiers: [
                    {
                      name: 'flip',
                      enabled: true,
                    },
                    {
                      name: 'preventOverflow',
                      enabled: true,
                      options: {
                        boundary: 'viewport',
                      },
                    },
                  ],
                  sx: {
                    zIndex: 1400,
                  },
                },
              }}
              getOptionDisabled={(option) => !!option.isExistingResponder}
              getOptionLabel={(option) =>
                option.isGroupHeader
                  ? option.first_name
                  : `${option.first_name} ${option.last_name}`.trim()
              }
              groupBy={(option) => option.groupName}
              filterOptions={filterOptions}
              isOptionEqualToValue={(option, value) =>
                option.auth_id === value.auth_id
              }
              renderOption={(props, option, { selected }) => {
                const { style: propStyle, ...otherProps } = props;

                // For group headers, show checkbox with group selection state
                if (option.isGroupHeader) {
                  const isFullySelected = isGroupFullySelected(option.groupId);
                  return (
                    <li
                      {...otherProps}
                      key={option.auth_id}
                      style={{
                        ...propStyle,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 16px',
                        boxSizing: 'border-box',
                        width: '100%',
                        maxWidth: '100%',
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f5f5f5';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <Checkbox
                        icon={icon}
                        checkedIcon={checkedIcon}
                        checked={isFullySelected}
                        sx={{ padding: 0, flexShrink: 0 }}
                      />
                      <Typography
                        sx={{
                          fontWeight: 600,
                          fontStyle: 'italic',
                          color: COLORS.SAFEGUARD_BLUE,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        {option.first_name}
                      </Typography>
                    </li>
                  );
                }

                // Regular user option
                return (
                  <li
                    {...otherProps}
                    key={option.auth_id}
                    style={{
                      ...propStyle,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 16px',
                      boxSizing: 'border-box',
                      width: '100%',
                      maxWidth: '100%',
                      cursor: option.isExistingResponder
                        ? 'default'
                        : 'pointer',
                      userSelect: 'none',
                      opacity: option.isExistingResponder ? 0.5 : 1,
                      pointerEvents: option.isExistingResponder
                        ? 'none'
                        : undefined,
                    }}
                    onMouseEnter={
                      option.isExistingResponder
                        ? undefined
                        : (e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                          }
                    }
                    onMouseLeave={
                      option.isExistingResponder
                        ? undefined
                        : (e) => {
                            e.currentTarget.style.backgroundColor =
                              'transparent';
                          }
                    }
                  >
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      checked={option.isExistingResponder || selected}
                      disabled={option.isExistingResponder}
                      sx={{ padding: 0, flexShrink: 0 }}
                    />
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                        flex: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 400,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {`${option.first_name} ${option.last_name}`.trim()}
                      </Typography>
                      {option.phone_number && (
                        <Typography
                          sx={{
                            color: COLORS.GREY,
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {option.phone_number}
                        </Typography>
                      )}
                    </Box>
                  </li>
                );
              }}
              renderGroup={(params) => (
                <li key={params.key}>
                  <Box
                    sx={{
                      position: 'sticky',
                      top: -8,
                      px: 2,
                      py: 1,
                      backgroundColor: COLORS.LIGHT_GRAY ?? '#f5f5f5',
                      fontWeight: 600,
                      color: COLORS.SAFEGUARD_BLUE ?? '#333',
                      borderBottom: `1px solid ${COLORS.GREY ?? '#ccc'}`,
                      zIndex: 1,
                    }}
                  >
                    {params.group}
                    {params.group ===
                      groups.find((g) => g.group_id === currentGroupId)
                        ?.group_name && (
                      <Chip
                        label="Current"
                        size="small"
                        sx={{
                          ml: 1,
                          backgroundColor: COLORS.SAFEGUARD_ORANGE,
                          color: COLORS.WHITE,
                          height: 20,
                          fontSize: 11,
                        }}
                      />
                    )}
                  </Box>
                  <Box component="ul" sx={{ p: 0 }}>
                    {params.children}
                  </Box>
                </li>
              )}
              renderInput={(params) => {
                const { InputProps, ...restParams } = params;
                return (
                  <TextField
                    {...restParams}
                    label={t(StringKeys.SEARCH_NAME_OR_PHONE)}
                    placeholder={t(StringKeys.SEARCH_USERS)}
                    slotProps={{
                      input: {
                        ...InputProps,
                        endAdornment: null,
                      },
                    }}
                  />
                );
              }}
              renderValue={() => null}
              sx={{ width: '100%' }}
            />
          </Box>

          {/* Right column: Selected users */}
          <Box sx={{ flex: 1, minWidth: 250 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 1,
              }}
            >
              <Typography variant="body2" sx={{ color: COLORS.GREY }}>
                {`${selectedUsers.length} ${t(StringKeys.WORKERS_SELECTED)}`}
              </Typography>
              {selectedUsers.length > 0 && (
                <Button
                  size="small"
                  onClick={() => setSelectedUsers([])}
                  sx={{ textTransform: 'none' }}
                >
                  {t(StringKeys.CLEAR_ALL)}
                </Button>
              )}
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 0.5,
                height: 300,
                overflowY: 'auto',
                py: 0.5,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                p: 1,
                justifyContent: 'flex-start',
                alignContent: 'flex-start',
                backgroundColor:
                  selectedUsers.length === 0 ? '#fafafa' : 'transparent',
              }}
            >
              {selectedUsers.length > 0 ? (
                selectedUsers.map((option) => (
                  <Chip
                    key={option.auth_id}
                    label={`${option.first_name} ${option.last_name}`.trim()}
                    size="small"
                    onDelete={() => {
                      setSelectedUsers(
                        selectedUsers.filter(
                          (u) => u.auth_id !== option.auth_id,
                        ),
                      );
                    }}
                  />
                ))
              ) : (
                <Typography
                  sx={{
                    color: COLORS.GREY,
                    fontStyle: 'italic',
                    width: '100%',
                    textAlign: 'center',
                    mt: 2,
                  }}
                >
                  {t(StringKeys.NO_SELECTED_WORKERS)}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 2,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
          '& > :not(:first-of-type)': {
            marginLeft: { xs: 0, sm: 1 },
          },
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          disabled={loading}
          fullWidth
          sx={{ order: { xs: 1, sm: 1 } }}
        >
          {t(StringKeys.CANCEL)}
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          color="secondary"
          disabled={selectedUsers.length === 0 || loading}
          fullWidth
          sx={{
            order: { xs: 2, sm: 2 },
          }}
        >
          {loading
            ? t(StringKeys.LOADING)
            : t(StringKeys.ADD_NEW_RESPONDERS, { count: selectedUsers.length })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserContactsModal;
